// Threat tags under the report title (RESTYLE §2): tinted square chips with a 2px edge in the status colour.
//   <ThreatTag sev? kind? href? title? className?>{label}</ThreatTag>
//       sev 1..5 (info..critical) picks the tint; kind "family" (bold) and "yara" (a "YARA" prefix) default to critical;
//       kind "technique" prints its MITRE id before the label when `id` is given. href makes it a link.
//   <ThreatTags items max? label? className? />   max ~6 (default 6), then a "+N" chip whose title lists the rest
//       items: { label, sev?, kind?, id?, href?, title?, key? }[] — order: family / YARA first, then techniques by severity
import type { ReactNode } from "react";
import type { SevLevel } from "./Glyphs";
import { cx } from "./cx";

type ThreatKind = "family" | "yara" | "technique" | "behavior";
export type ThreatTagItem = { label: string; sev?: SevLevel; kind?: ThreatKind; id?: string; href?: string; title?: string; key?: string };

export function ThreatTag({
  sev,
  kind = "behavior",
  id,
  href,
  title,
  className,
  children,
}: {
  sev?: SevLevel;
  kind?: ThreatKind;
  id?: string;
  href?: string;
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  const level = sev ?? (kind === "family" || kind === "yara" ? 5 : 1);
  const body = (
    <>
      {kind === "yara" && <span className="tkey">Yara</span>}
      {kind === "technique" && id && <span className="tid">{id}</span>}
      <span>{children}</span>
    </>
  );
  const cls = cx("ttag", kind === "family" && "ttag--family", className);
  if (href)
    return (
      <a className={cls} data-sev={level} href={href} title={title} target="_blank" rel="noreferrer">
        {body}
      </a>
    );
  return (
    <span className={cls} data-sev={level} title={title}>
      {body}
    </span>
  );
}

export function ThreatTags({ items, max = 6, label = "Threat tags", className }: { items: readonly ThreatTagItem[]; max?: number; label?: string; className?: string }) {
  if (!items.length) return null;
  const shown = items.slice(0, max);
  const rest = items.slice(max);
  return (
    <ul className={cx("ttags", className)} aria-label={label}>
      {shown.map((t, i) => (
        <li key={t.key ?? `${t.kind ?? "b"}-${t.id ?? ""}-${t.label}-${i}`}>
          <ThreatTag sev={t.sev} kind={t.kind} id={t.id} href={t.href} title={t.title}>
            {t.label}
          </ThreatTag>
        </li>
      ))}
      {rest.length > 0 && (
        <li>
          <span className="ttag ttag--more" title={rest.map((t) => (t.id ? `${t.id} ${t.label}` : t.label)).join("\n")}>
            +{rest.length}
            <span className="sr-only"> more: {rest.map((t) => t.label).join(", ")}</span>
          </span>
        </li>
      )}
    </ul>
  );
}
